import type { TovDefinitionEvidenceResponse } from "contracts/api/client-tov/evidence.responses";
import type { TovSourceResponse } from "contracts/api/client-tov/sources.responses";
import { For, Show } from "solid-js";
import { TOV_GUIDELINE_STATE, TOV_SOURCE_STATE } from "../../enums/tov";
import type {
  GuidelineCard,
  SourceDetail,
  SourceTrust,
} from "../tov/guidelineModel";
import { sourceStatusLabel } from "../tov/statusLabels";
export function GuidelineCardGrid(props: {
  cards: GuidelineCard[];
  emptyTitle: string;
  emptyText: string;
  onInspect: (item: GuidelineCard, link: TovDefinitionEvidenceResponse) => void;
}) {
  return (
    <Show
      when={props.cards.length > 0}
      fallback={<EmptyState title={props.emptyTitle} text={props.emptyText} />}
    >
      <div class="signal-guideline-card-grid">
        <For each={props.cards}>
          {(item) => (
            <article class="signal-guideline-card">
              <div class="signal-guideline-card-head">
                <span>{item.type}</span>
                <span
                  class={
                    item.sourceTrust.state === TOV_GUIDELINE_STATE.REVIEW
                      ? "is-review"
                      : ""
                  }
                >
                  {item.sourceTrust.label}
                </span>
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <Show when={item.detail}>
                {(detail) => (
                  <details class="signal-guideline-technical">
                    <summary>Technical detail</summary>
                    <p class="signal-muted">{detail()}</p>
                  </details>
                )}
              </Show>
              <dl>
                <div>
                  <dt>Category</dt>
                  <dd>{item.category}</dd>
                </div>
                <div>
                  <dt>Source</dt>
                  <dd>
                    {item.source ??
                      (item.sourceState === TOV_SOURCE_STATE.NOT_ESTABLISHED
                        ? "Source not established"
                        : "No linked source")}
                  </dd>
                </div>
                <div>
                  <dt>Evidence</dt>
                  <dd>
                    {item.sourceState === TOV_SOURCE_STATE.LINKED
                      ? "Linked"
                      : item.sourceState === TOV_SOURCE_STATE.NOT_ESTABLISHED
                        ? "Source not established"
                        : "No linked evidence"}
                  </dd>
                </div>
              </dl>
              <Show
                when={item.evidence[0]}
                fallback={
                  <button type="button" disabled>
                    View evidence unavailable
                  </button>
                }
              >
                {(link) => (
                  <button
                    type="button"
                    class="signal-inline-action"
                    onClick={() => props.onInspect(item, link())}
                  >
                    View evidence
                  </button>
                )}
              </Show>
            </article>
          )}
        </For>
      </div>
    </Show>
  );
}

export function SourceRow(props: {
  source: TovSourceResponse;
  detail: SourceDetail | undefined;
  evidenceCount: number;
  trust: SourceTrust;
}) {
  return (
    <article class="signal-source-row">
      <div>
        <strong>{props.source.title}</strong>
        <p>
          {props.source.sourceType} / {sourceStatusLabel(props.source.status)}
        </p>
      </div>
      <dl>
        <div>
          <dt>Pages</dt>
          <dd>{props.detail ? `${props.detail.parsedPages}` : "Checking"}</dd>
        </div>
        <div>
          <dt>Chunks</dt>
          <dd>{props.detail ? `${props.detail.chunks}` : "Checking"}</dd>
        </div>
        <div>
          <dt>Evidence</dt>
          <dd>{props.evidenceCount}</dd>
        </div>
        <div>
          <dt>Review</dt>
          <dd>
            {props.trust.state === TOV_GUIDELINE_STATE.REVIEW
              ? "Required"
              : props.trust.state === TOV_GUIDELINE_STATE.UNKNOWN
                ? "Source not established"
                : "Linked"}
          </dd>
        </div>
      </dl>
    </article>
  );
}

export function EmptyState(props: { title: string; text: string }) {
  return (
    <div class="signal-guideline-empty">
      <strong>{props.title}</strong>
      <p>{props.text}</p>
    </div>
  );
}
